/**
 * The colours and the letters of a faction's emblem.
 *
 * Beside `FactionEmblem.tsx` for the same reason `natoSymbols.ts` is beside
 * `NatoSymbol.tsx`: a file that exports components exports only components.
 */

/** APP-6 frames, so a faction is drawn on the same vocabulary the map markers use. */
export type SideKey = 'west' | 'east' | 'resistance' | 'civilian' | 'unknown';

export function sideOf(side: string | null | undefined): SideKey {
  switch ((side ?? '').toLowerCase()) {
    case 'west':
    case 'blufor':
      return 'west';
    case 'east':
    case 'opfor':
      return 'east';
    case 'resistance':
    case 'independent':
    case 'guer':
      return 'resistance';
    case 'civilian':
    case 'civ':
      return 'civilian';
    default:
      return 'unknown';
  }
}

/** The stroke of a frame, matching the chips the same pages already paint per side. */
export const SIDE_COLOUR: Record<SideKey, string> = {
  west: '#6f9cc9',
  east: '#e0524f',
  resistance: '#3ec27a',
  civilian: '#c9c2b4',
  unknown: '#9aa4b0',
};

/**
 * The two colours of a faction's own flag.
 *
 * A faction's real flag lives in the game's data as a `.paa` texture, which a
 * web page cannot read (the same wall the map markers hit, `NatoSymbol.tsx`),
 * and the platform will not ship somebody else's art. So the emblem is drawn:
 * a wash the faction always comes out of, taken from its class name, and the
 * side's colour around it.
 *
 * The vanilla families are named on purpose, because "the blue one" and "the
 * red one" is how everybody talks about them and a hash would have made NATO
 * teal and CSAT purple. Anything else — and every mod's faction is anything
 * else — is spread around the wheel by its own class name, which at least makes
 * two factions of the same mod look different from each other.
 */
export function coloursOf(factionClass: string): [string, string] {
  const cls = (factionClass ?? '').toUpperCase();
  if (cls.startsWith('BLU')) return ['#1f3a5c', '#2f5f92'];
  if (cls.startsWith('OPF')) return ['#4a1f22', '#8c3a33'];
  if (cls.startsWith('IND')) return ['#2b3a22', '#4f6b39'];
  if (cls.startsWith('CIV')) return ['#3a3730', '#5d574c'];
  let hash = 0;
  for (let i = 0; i < cls.length; i++) hash = (hash * 31 + cls.charCodeAt(i)) % 360;
  return [`hsl(${hash} 38% 20%)`, `hsl(${(hash + 28) % 360} 42% 34%)`];
}

/**
 * The letters on the emblem: the initials of the words of the name, or the
 * first two characters when it is a single word. Three at most, because more
 * than three is unreadable at the size a card draws this.
 */
export function initialsOf(name: string): string {
  const words = (name ?? '')
    .trim()
    .split(/[\s_\-·.]+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}
